import { Link } from "react-router-dom";
import { ArrowLeft, ShieldCheck, Check, X, Eye, Mail, Lock, Server, FileSearch, ExternalLink } from "lucide-react";
import { Button } from "../components/ui/button";

// Two-column "we collect / we do NOT collect" pattern — the industry-standard
// transparency layout journalists and privacy advocates love to see.
const CollectRow = ({ collected, label, note }) => (
  <li className="flex items-start gap-3 py-3 border-b border-[#1A2030] last:border-b-0">
    <span className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 ${collected ? "bg-[#00E68A]/15" : "bg-[#D14A85]/15"}`}>
      {collected ? <Check className="w-3 h-3 text-[#00E68A]" /> : <X className="w-3 h-3 text-[#D14A85]" />}
    </span>
    <div className="min-w-0">
      <p className="text-sm text-white font-medium leading-tight">{label}</p>
      {note && <p className="text-xs text-[#8A92A0] mt-1 leading-snug">{note}</p>}
    </div>
  </li>
);

const Section = ({ icon: Icon, title, subtitle, children, testid }) => (
  <section className="card p-6 sm:p-8" data-testid={testid}>
    <div className="flex items-start gap-4 mb-4">
      <div className="w-11 h-11 rounded-xl bg-[#EC008C]/10 border border-[#EC008C]/25 flex items-center justify-center flex-shrink-0">
        <Icon className="w-5 h-5 text-[#EC008C]" />
      </div>
      <div>
        <h2 className="font-['Unbounded'] text-lg sm:text-xl font-semibold text-white leading-tight">{title}</h2>
        {subtitle && <p className="text-xs text-[#8A92A0] mt-1">{subtitle}</p>}
      </div>
    </div>
    <div className="ml-0 sm:ml-[60px]">{children}</div>
  </section>
);

const PrivacyReport = () => {
  const auditDate = "February 2026";

  return (
    <div className="min-h-screen bg-[#050810]" data-testid="privacy-report-page">
      <header className="fixed top-0 left-0 right-0 z-50 glass-strong">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/home" className="flex items-center gap-2" data-testid="logo-link">
            <img src="/logo.png" alt="StardomFans" className="w-10 h-10 object-contain" style={{ mixBlendMode: "lighten" }} />
          </Link>
          <Link to="/home">
            <Button variant="ghost" className="text-[#8A92A0] hover:text-white" data-testid="back-home-btn">
              <ArrowLeft className="w-4 h-4 mr-2" /> Back to Home
            </Button>
          </Link>
        </div>
      </header>

      <main className="pt-32 pb-16 px-6">
        <div className="max-w-4xl mx-auto">
          {/* Hero */}
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#00E68A]/10 border border-[#00E68A]/30 mb-6">
              <ShieldCheck className="w-4 h-4 text-[#00E68A]" />
              <span className="text-sm text-[#00E68A] font-semibold tracking-wide">PRIVACY REPORT · 100/100</span>
            </div>
            <h1 className="font-['Unbounded'] text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-5 leading-tight">
              Exactly what we collect.<br />
              <span className="text-[#EC008C] italic">And what we don&apos;t.</span>
            </h1>
            <p className="text-[#8A92A0] text-base sm:text-lg max-w-2xl mx-auto leading-relaxed">
              This is our open audit — line by line, what StardomFans.com stores about you, and every mainstream
              tracker or data broker we&apos;ve deliberately kept off the platform.
            </p>
            <p className="text-xs text-[#6A7280] mt-4">Last audited: {auditDate} · Self-audited monthly · Updated whenever anything changes.</p>
          </div>

          {/* The 3 Signals */}
          <div className="grid sm:grid-cols-3 gap-3 mb-10" data-testid="privacy-report-signals">
            {[
              { icon: Eye, k: "0 Trackers", v: "No PostHog, GA, Meta Pixel, TikTok/Instagram embed scripts, or session recorders." },
              { icon: Lock, k: "No 3rd-Party Sign-In", v: "Your account never touches Google, Meta, or any OAuth broker. Email + password only." },
              { icon: Mail, k: "No Outbound Email", v: "All correspondence flows through the in-app Admin Inbox. No marketing email, no newsletters, no receipts by email." },
            ].map(({ icon: Icon, k, v }) => (
              <div key={k} className="p-4 rounded-xl border border-[#00E68A]/25 bg-[#00E68A]/[0.04]">
                <div className="flex items-center gap-2 mb-2">
                  <Icon className="w-4 h-4 text-[#00E68A]" />
                  <p className="text-sm font-semibold text-white">{k}</p>
                </div>
                <p className="text-xs text-[#8A92A0] leading-snug">{v}</p>
              </div>
            ))}
          </div>

          <div className="space-y-6">
            {/* Collected */}
            <Section icon={FileSearch} title="What we do collect" subtitle="Only what's needed to run your account and process payments." testid="section-collected">
              <ul>
                <CollectRow collected label="Email address" note="Used exclusively for sign-in and account-recovery lookups. Never emailed marketing content." />
                <CollectRow collected label="Password (as a bcrypt hash)" note="Salted + hashed with bcrypt. Not readable by anyone — including our engineers." />
                <CollectRow collected label="Display name & profile picture" note="If you set one. Shown on posts and your profile." />
                <CollectRow collected label="Content you upload" note="Posts, music, videos, product listings, event details — you own them, and you can delete them at any time." />
                <CollectRow collected label="Payment tokens (not card numbers)" note="Actual card data is handled 100% by our PCI-DSS Level 1 payment processor. We store only opaque IDs." />
                <CollectRow collected label="Session token (first-party cookie)" note="A random opaque string that logs you in. Rotates automatically. Not shared with anyone." />
                <CollectRow collected label="Server logs (IP + user-agent, security only)" note="Kept short-term for abuse prevention (brute-force lockouts, DDoS). Never joined to a marketing profile." />
              </ul>
            </Section>

            {/* Not collected */}
            <Section icon={ShieldCheck} title="What we deliberately DO NOT collect" subtitle="These are on-by-default on most modern web apps — and off here." testid="section-not-collected">
              <ul>
                <CollectRow collected={false} label="Behavioural analytics" note="No PostHog, Google Analytics, Amplitude, Mixpanel, Segment, Heap. No session recordings. No heatmaps." />
                <CollectRow collected={false} label="Ad-tech pixels" note="No Meta / Facebook Pixel, no TikTok Pixel, no Snap Pixel, no LinkedIn Insight Tag, no Twitter/X pixel." />
                <CollectRow collected={false} label="3rd-party embed trackers" note="TikTok & Instagram videos render as static preview cards. Twitter/X renders via react-tweet (no widgets.js). YouTube uses youtube-nocookie.com." />
                <CollectRow collected={false} label="3rd-party sign-in / social login" note="No Google, Meta, Apple, or Emergent OAuth. Your identity never leaves our own auth stack." />
                <CollectRow collected={false} label="Outbound marketing email" note="No newsletters, no promo blasts, no lifecycle drips, no receipts by email. Everything happens inside your Admin Inbox." />
                <CollectRow collected={false} label="Data-broker enrichment" note="We never buy or enrich your record from Clearbit, ZoomInfo, Apollo, etc." />
                <CollectRow collected={false} label="Phone numbers, addresses, ID docs" note="Not required to create an account, browse, or transact (our processor collects billing details directly at checkout)." />
                <CollectRow collected={false} label="Cross-site cookies / fingerprints" note="No canvas / audio / font fingerprinting, no third-party cookies, no supercookies." />
                <CollectRow collected={false} label="Location / precise GPS" note="Never requested. Never stored." />
              </ul>
            </Section>

            {/* Where it lives */}
            <Section icon={Server} title="Where your data lives" subtitle="Encrypted at rest and in transit — with independent, well-known providers." testid="section-hosting">
              <ul className="list-disc list-inside space-y-2 text-sm text-[#8A92A0]">
                <li><strong className="text-white">Application &amp; database</strong> — hosted on our managed cloud infrastructure with daily backups. Data in transit is TLS 1.2+ end-to-end.</li>
                <li><strong className="text-white">Payment data</strong> — held and encrypted by our PCI-DSS Level 1 payment processor. We only ever see opaque customer / payment intent IDs.</li>
                <li><strong className="text-white">Media uploads</strong> — stored in a cloud object store, served via HTTPS.</li>
                <li><strong className="text-white">No overseas processors</strong> — no data is sold or forwarded to advertising or data-broker networks in any jurisdiction.</li>
              </ul>
            </Section>

            {/* Your rights */}
            <Section icon={Lock} title="Your data, your call" subtitle="You are in charge — always." testid="section-rights">
              <ul className="list-disc list-inside space-y-2 text-sm text-[#8A92A0]">
                <li><strong className="text-white">Export</strong> — request a full copy of your account data from your Dashboard &rarr; Security page.</li>
                <li><strong className="text-white">Delete</strong> — one-click account deletion. All non-legally-retained data is purged.</li>
                <li><strong className="text-white">Correct</strong> — edit anything on your profile at any time.</li>
                <li><strong className="text-white">Reach us</strong> — message the team via the in-app Admin Inbox. We do not maintain a public support email address.</li>
              </ul>
            </Section>

            {/* CTA */}
            <div className="card p-8 text-center bg-gradient-to-br from-[#00E68A]/10 via-transparent to-[#00E68A]/5 border-[#00E68A]/30" data-testid="verify-cta">
              <ShieldCheck className="w-10 h-10 text-[#00E68A] mx-auto mb-4" />
              <h3 className="font-['Unbounded'] text-xl sm:text-2xl font-bold text-white mb-3">
                Verify it yourself.
              </h3>
              <p className="text-[#8A92A0] mb-6 max-w-xl mx-auto text-sm">
                Open your browser&apos;s DevTools &rarr; Network tab &rarr; browse StardomFans.com.
                You&apos;ll see requests to <code className="text-[#00E68A] bg-[#00E68A]/10 px-1.5 py-0.5 rounded text-xs">stardomfans.com</code> and our secure payment processor.
                Nothing else. No <code className="text-[#8A92A0] bg-[#1A2030] px-1.5 py-0.5 rounded text-xs line-through">googletagmanager</code>,
                no <code className="text-[#8A92A0] bg-[#1A2030] px-1.5 py-0.5 rounded text-xs line-through">posthog</code>,
                no <code className="text-[#8A92A0] bg-[#1A2030] px-1.5 py-0.5 rounded text-xs line-through">facebook.net</code>.
              </p>
              <div className="flex flex-wrap gap-3 justify-center">
                <Link to="/security">
                  <Button variant="outline" className="border-[#1A2030] text-white hover:border-[#00E68A]/50 rounded-full px-6 h-11" data-testid="see-security-btn">
                    Read the Full Security Page <ExternalLink className="w-3.5 h-3.5 ml-2" />
                  </Button>
                </Link>
                <Link to="/privacy">
                  <Button variant="outline" className="border-[#1A2030] text-white hover:border-[#00E68A]/50 rounded-full px-6 h-11" data-testid="see-privacy-btn">
                    Read the Privacy Policy <ExternalLink className="w-3.5 h-3.5 ml-2" />
                  </Button>
                </Link>
              </div>
            </div>

            <p className="text-xs text-[#6A7280] text-center pt-2">
              Last audited: {auditDate} · Living document — updated whenever we ship a change that affects data collection.
            </p>
          </div>
        </div>
      </main>

      <footer className="py-12 px-6 border-t border-[#1A2030]">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <img src="/logo.png" alt="SF" className="w-10 h-10 object-contain" style={{ mixBlendMode: "lighten" }} />
          <div className="flex gap-6 flex-wrap justify-center">
            <Link to="/terms" className="text-[#8A92A0] hover:text-[#EC008C] text-sm">Terms</Link>
            <Link to="/privacy" className="text-[#8A92A0] hover:text-[#EC008C] text-sm">Privacy</Link>
            <Link to="/copyright" className="text-[#8A92A0] hover:text-[#EC008C] text-sm">Copyright</Link>
            <Link to="/security" className="text-[#8A92A0] hover:text-[#EC008C] text-sm">Security</Link>
            <Link to="/privacy-report" className="text-[#00E68A] text-sm font-semibold">Privacy Report</Link>
          </div>
          <p className="text-[#8A92A0] text-sm">&copy; {new Date().getFullYear()} StardomFans Ltd.</p>
        </div>
      </footer>
    </div>
  );
};

export default PrivacyReport;
